import React, { useMemo, useState, useCallback, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { DataGrid } from '@mui/x-data-grid';
import Button from '@mui/material/Button';
import { container, dataGridStyles, iconButtonStyle } from 'pages/DashboardHelpers';
import { DEFAULT_PAGE_SIZE } from 'constants/configConstants';
import CustomPagination from 'pages/ocplcDashboard/CustomPagination';
import { getRowClassName, pageSizeOptions } from 'pages/ocplcDashboard/ocplcHelpers';
import { useDispatch, useSelector } from 'react-redux';
import { clearModelHrcyFilters, filterModelHrcy, getModelHierarchy } from 'actions/userActions';
import ModelColumns from './modelHrcyColumns';
import { AddModelHrcyDialog } from './AddModelHrcyDialog';
import { DeleteModelHrcyDialog } from './deleteModelHrcyDialog';
import { UpdateModelHrcyDialog } from './updateModelHrcyDialog';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import AddIcon from '@mui/icons-material/Add';
import { REGIONS } from 'constants/ocplcConstants';

function ModelHierarchy() {
  const dispatch = useDispatch();

  const modelHierarchy = useSelector(state => state.user?.filteredModelHrcy || []);
  const categoryList = useSelector(state => state.user?.modelHierarchy?.categoryList || []);
  const seriesList = useSelector(state => state.user?.modelHierarchy?.seriesList || []);
  const subCategoryList = useSelector(state => state.user?.modelHierarchy?.subCategoryList || []);
  const email = useSelector(state => state.user?.kbUser?.email);
  const loading = useSelector(state => state.user?.loading);
  const filters = useSelector(state => state.user?.filters || {});

  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openUpdateDialog, setOpenUpdateDialog] = useState(false);
  const [selectedRow, setSelectedRow] = useState({});
  const [checkedRows, setCheckedRows] = useState([]);

  const handleEdit = row => { setSelectedRow(row); setOpenUpdateDialog(true); };
  const handleDelete = row => { setSelectedRow(row); setOpenDeleteDialog(true); };
  const handleClose = () => { setOpenAddDialog(false); setOpenDeleteDialog(false); setOpenUpdateDialog(false); setSelectedRow(null); };

  const handleChange = useCallback((col, value) => {
    dispatch(filterModelHrcy({ ...col, searchText: value }));
  }, []);

  const handleRowSelection = newSelection => setCheckedRows(newSelection);

  useEffect(() => {
    dispatch(getModelHierarchy());
  }, [dispatch]);

    const handleClearFilters = () => {
      dispatch(clearModelHrcyFilters());
      setCheckedRows([]);
    };
  


  const columns = useMemo(
  () =>
    ModelColumns({
      filters,
      handleChange,
      handleEdit,
      handleDelete,
      REGIONS,
      categoryList,
      subCategoryList,
      seriesList
    }),
  [filters, handleChange, handleEdit, handleDelete, REGIONS, categoryList, subCategoryList, seriesList]
);

    return (
    <Box sx={{ ...container, minWidth: '100%', marginTop: 2 }} className='model-hierarchy-page'>
      <Typography variant='h5'>Model Hierarchy</Typography>
      <Box
  className='modelHrcy-actions-box'
  sx={{
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    mb: 2
  }}
>
  {/* Left side */}
  <Box sx={{ display: 'flex', alignItems: 'center',padding: '8px 16px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
    <Box sx={{ mr: 2 }}>
      <Typography sx={{ fontSize: "19px" }}>Create new model</Typography>
      <Typography sx={{ color: '#b8b8b8', fontSize: "13px" }}>Add new model to KB-IDE</Typography>
    </Box>
    <Button variant="text" onClick={() => setOpenAddDialog(true)}> <AddIcon fontSize='small'/> Add Model</Button>
  </Box>
  {/* Right side */}
  <Box sx={{ display: 'flex', gap: 1 }}>
    <Button
      color="primary"
      sx={iconButtonStyle}
      disableRipple
      onClick={handleClearFilters}
      endIcon={<FilterAltOffIcon />}
    >
      Clear Filters
    </Button>
  </Box>
</Box>
        <DataGrid
          columns={columns}
          rows={modelHierarchy}
          loading={loading}
          getRowId={row => row.kmatId + row.region + row.category + row.series}
          columnHeaderHeight={90}
          rowHeight={48}
          className='dashboard-data-grid'
          getRowClassName={getRowClassName}
          headerClassName='dashboard-grid-headers'
          slots={{ pagination: props => <CustomPagination {...props} pageSizeOptions={pageSizeOptions} /> }}
          disableSelectionOnClick
          onRowSelectionModelChange={handleRowSelection}
          rowSelectionModel={checkedRows}
          initialState={{ pagination: { paginationModel: { page: 0, pageSize: DEFAULT_PAGE_SIZE } } }}
          sx={dataGridStyles}
          disableColumnFilter
          disableColumnMenu
          hideFooterSelectedRowCount
        />
        {openAddDialog && <AddModelHrcyDialog open={openAddDialog} onClose={handleClose} regions={REGIONS} seriesList={seriesList} categoryList={categoryList} subCategoryList={subCategoryList} email={email} />}
        {openUpdateDialog && <UpdateModelHrcyDialog open={openUpdateDialog} onClose={handleClose} selectedRow={selectedRow} email={email} />}
        {openDeleteDialog && <DeleteModelHrcyDialog open={openDeleteDialog} onClose={handleClose} selectedRow={selectedRow} email={email} />}
    </Box>
  );
}

export default ModelHierarchy;